
import { format } from "date-fns";
import { CalendarIcon, Clock, UsersIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUserStore } from "@/store/userStore";
import { useSettingsStore } from "@/store/api/settingsStore";

interface BookingDetailsTabProps {
  selectedDate: Date;
  selectedTime: string;
  handleTimeChange: (time: string) => void;
  availableSlots: number;
  handleSlotsChange: (slots: number) => void;
  dateLocale: any;
  totalCapacity: number;
  duration?: string;
}

export function BookingDetailsTab({
  selectedDate,
  selectedTime,
  handleTimeChange,
  availableSlots,
  handleSlotsChange,
  dateLocale,
  totalCapacity,
  duration
}: BookingDetailsTabProps) {
  const { t } = useTranslation(["classes", "common"]);
  const { role } = useUserStore();
  const isAdmin = role === 'admin';
  const { settings } = useSettingsStore();
  const hideAvailableSlots = settings?.schedule?.hideAvailableSlotsForClients && !isAdmin;
  
  return (
    <div className="py-4 space-y-4 min-w-0 overflow-hidden w-full">
      <div className="grid grid-cols-2 gap-3 min-w-0 w-full">
        <div className="border rounded-md p-3 min-w-0 overflow-hidden">
          <div className="flex items-center text-sm text-muted-foreground mb-1">
            <CalendarIcon className="h-4 w-4 mr-2 shrink-0" />
            {t('date', { ns: 'common' })}
          </div>
          <div className="font-medium truncate">
            {format(selectedDate, 'EEEE, MMMM d', { locale: dateLocale })}
          </div>
        </div>
        
        <div className="border rounded-md p-3 min-w-0 overflow-hidden">
          <div className="flex items-center text-sm text-muted-foreground mb-1">
            <Clock className="h-4 w-4 mr-2 shrink-0" />
            {t('time', { ns: 'common' })}
          </div>
          <div className="font-medium min-w-0 overflow-hidden">
            <Input
              type="time"
              value={selectedTime}
              onChange={(e) => handleTimeChange(e.target.value)}
              className="h-8 p-1 w-full min-w-0 max-w-full"
              disabled={!isAdmin}
            />
          </div>
        </div>

        {(isAdmin || !hideAvailableSlots) && (
          <div className="border rounded-md p-3 min-w-0 overflow-hidden">
            <div className="flex items-center text-sm text-muted-foreground mb-1">
              <UsersIcon className="h-4 w-4 mr-2 shrink-0" />
              {t('booking.availableSlots', { ns: 'classes' })}
            </div>
            {isAdmin ? (
              <div className="font-medium">
                {availableSlots} / {totalCapacity}
              </div>
            ) : (
              <div className="font-medium">
                {availableSlots}
              </div>
            )}
          </div>
        )}

        {duration && (
          <div className="border rounded-md p-3 min-w-0 overflow-hidden">
            <div className="flex items-center text-sm text-muted-foreground mb-1">
              <Clock className="h-4 w-4 mr-2 shrink-0" />
              {t('booking.duration', { ns: 'classes' })}
            </div>
            <div className="font-medium">
              {duration} {t('minutes', { ns: 'classes' })}
            </div>
          </div>
        )}
      </div>
      
      {/* Only show capacity input for admin users */}
      {isAdmin && handleSlotsChange && (
        <div className="mt-2">
          <Label htmlFor="capacity" className="text-sm">
            {t('booking.capacity', { ns: 'classes' })}
          </Label>
          <Input
            id="capacity"
            type="number"
            min="0"
            max="100"
            value={totalCapacity}
            onChange={(e) => handleSlotsChange(parseInt(e.target.value))}
            className="mt-1"
          />
        </div>
      )}
    </div>
  );
}
